<script lang="ts">
  import { onMount } from 'svelte';
  import { push } from 'svelte-spa-router';
  import Table from '../../Table.svelte';
  import Button from '../../Button.svelte';
  import Pagination from '../../Pagination.svelte';
  import { PayoutService, type DonationPayout } from '../../../lib/api/charityServices';
  import { refreshCharityPendingConfirmation } from '../../../stores/charity-pending-confirmation.store';
  import BackButton from '../../BackButton.svelte';
  import Stepper from '../../Stepper.svelte';
  import StageBadge from '../../StageBadge.svelte';
  import type { LifecycleStage } from '../../../lib/donationLifecycle';
  // Get payoutId from component props
  export let params: { id: string };

  // Loading state
  let loading = true;
  let error: string | null = null;
  let markReceivedError: string | null = null;
  let markingReceived = false;
  let showMarkReceivedConfirm = false;
  let markReceivedConfirmEscapeHandler: ((e: KeyboardEvent) => void) | null = null;
  let currentPage = 1;
  let perPage = 10; // Reduced items per page to better demonstrate pagination
  let totalPages = 1;
  let loadingDonations = false;

  // Payout details
  let payoutDetail: DonationPayout | null = null;
  let donationBreakdown: Array<any> = [];

  // Canonical lifecycle stage for this payout: a payout is money already paid to the charity,
  // so it is either received (charity confirmed) or paid_to_charity (awaiting confirmation).
  $: stage = ((payoutDetail?.received || payoutDetail?.charity_marked_received_at)
    ? 'received_by_charity'
    : 'paid_to_charity') as LifecycleStage;

  // Table columns configuration for donation breakdown
  const columns = [
    {
      key: 'employee_name',
      label: 'Employee Name',
      width: '20%',
      render: (value: string) => `<span>${value || 'N/A'}</span>`
    },
    {
      key: 'employer_name',
      label: 'Employer',
      width: '20%',
      render: (value: string) => `<span>${value || 'N/A'}</span>`
    },
    {
      key: 'hours',
      label: 'Hours',
      width: '15%',
      render: (value: number) => `<span>${value} hrs</span>`
    },
    {
      key: 'value',
      label: 'Employee Value',
      width: '15%',
      render: (value: number) => `<span class="donation-amount">${formatCurrency(value)}</span>`
    },
    {
      key: 'employer_top_up',
      label: 'Employer Match Value',
      width: '15%',
      render: (value: number) => `<span class="donation-amount">${formatCurrency(value)}</span>`
    },
    // {
    //   key: 'date_donated',
    //   label: 'Date',
    //   width: '15%',
    //   render: (value: string) => `<span>${formatDate(value)}</span>`
    // },
    // {
    //   key: 'status',
    //   label: 'Status',
    //   width: '15%',
    //   render: (value: string) => {
    //     return `<div class="status-badge status-${getStatusClass(value)}">
    //       ${getStatusIcon(value)}
    //       <span>${value}</span>
    //     </div>`;
    //   }
    // }
  ];

  // Format date
  function formatDate(dateString: string | undefined): string {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  }

  // Format currency (values are already in dollars)
  function formatCurrency(amount: string | number | undefined): string {
    if (amount === undefined || amount === null) return '$0.00';

    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(numAmount);
  }

  // Load payout details
  async function loadPayoutDetails() {
    loading = true;
    error = null;
    markReceivedError = null;

    try {
      if (params?.id) {
        const result = await PayoutService.getPayoutDetail(params.id, currentPage, perPage);
        
        if (result.success && result.payoutDetail) {
          payoutDetail = result.payoutDetail;
          
          // Extract donation breakdown from the nested structure
          if (result.payoutDetail.donation_breakdown && result.payoutDetail.donation_breakdown.data) {
            donationBreakdown = result.payoutDetail.donation_breakdown.data;
          } else if (result.payoutDetail.donation_breakdown) {
            // Handle case where donation_breakdown is directly an array
            donationBreakdown = Array.isArray(result.payoutDetail.donation_breakdown) 
              ? result.payoutDetail.donation_breakdown 
              : [];
          } else {
            donationBreakdown = [];
          }
          
          // Calculate total pages based on total donations or paging info
          if (result.payoutDetail.donation_breakdown && result.payoutDetail.donation_breakdown.paging) {
            const paging = result.payoutDetail.donation_breakdown.paging;
            totalPages = paging.last_page || Math.ceil(paging.total / paging.per_page);
          } else if (payoutDetail.total_donations) {
            totalPages = Math.ceil(payoutDetail.total_donations / perPage);
          }
        } else {
          error = result.error || 'Failed to load payout details';
        }
      } else {
        error = 'Invalid payout ID';
      }
    } catch (err) {
      console.error('Error loading payout details:', err);
      error = 'Failed to load payout details';
    } finally {
      loading = false;
    }
  }

  // Load donation breakdown for specific page
  async function loadDonationBreakdown(page: number) {
    if (!payoutDetail) return;
    
    loadingDonations = true;
    
    try {
      const result = await PayoutService.getPayoutDetail(params.id, page, perPage);
      
      if (result.success && result.payoutDetail) {
        // Extract donation breakdown from the nested structure
        if (result.payoutDetail.donation_breakdown && result.payoutDetail.donation_breakdown.data) {
          donationBreakdown = result.payoutDetail.donation_breakdown.data;
        } else if (result.payoutDetail.donation_breakdown) {
          // Handle case where donation_breakdown is directly an array (fallback)
          donationBreakdown = Array.isArray(result.payoutDetail.donation_breakdown) 
            ? result.payoutDetail.donation_breakdown 
            : [];
        } else {
          donationBreakdown = [];
        }
        
        currentPage = page;
        
        // Update pagination info
        if (result.payoutDetail.donation_breakdown && result.payoutDetail.donation_breakdown.paging) {
          const paging = result.payoutDetail.donation_breakdown.paging;
          totalPages = paging.last_page || Math.ceil(paging.total / paging.per_page);
        }
      } else {
        // Show error but don't reset the current donations
        console.error('Failed to load donations for page', page);
      }
    } catch (err) {
      console.error('Error loading donations:', err);
    } finally {
      loadingDonations = false;
    }
  }

  // Handle page change
  function handlePageChange(event: CustomEvent) {
    const { page } = event.detail;
    loadDonationBreakdown(page);
  }

  // Handle back button click
  function goBack() {
    const received = payoutDetail?.received || payoutDetail?.charity_marked_received_at;
    push(received ? '/payouts' : '/pending-confirmation');
  }

  // Get the display status based on backend status and received flag
  function getDisplayStatus(payout: DonationPayout): string {
    const status = (payout.donation_payout_status?.name || payout.status?.name || '').toUpperCase();
    if (status === 'COMPLETED') {
      if (payout.received || payout.charity_marked_received_at) {
        return 'Confirmed Received';
      }
      return 'Pending Receival Confirmation';
    }
    return payout.donation_payout_status?.name || payout.status?.name || 'Pending';
  }

  // Get status badge class
  function getStatusClass(displayStatus: string): string {
    switch (displayStatus) {
      case 'Confirmed Received':
        return 'received';
      case 'Pending Receival Confirmation':
        return 'pending-receival';
      default:
        break;
    }
    switch (displayStatus.toUpperCase()) {
      case 'COMPLETED':
      case 'APPROVED':
        return 'approved';
      case 'FAILED':
      case 'REJECTED':
        return 'rejected';
      case 'PENDING':
      default:
        return 'pending';
    }
  }

  // Get status icon based on status
  function getStatusIcon(displayStatus: string): string {
    switch (displayStatus) {
      case 'Confirmed Received':
        return '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>';
      case 'Pending Receival Confirmation':
        return '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z"/><path d="M12.5 7H11v6l5.25 3.15.75-1.23-4.5-2.67z"/></svg>';
      default:
        break;
    }
    switch (displayStatus.toUpperCase()) {
      case 'COMPLETED':
      case 'APPROVED':
        return '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>';
      case 'FAILED':
      case 'REJECTED':
        return '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>';
      case 'PENDING':
      default:
        return '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z"/><path d="M12.5 7H11v6l5.25 3.15.75-1.23-4.5-2.67z"/></svg>';
    }
  }

  // Transform donation breakdown data for table display
  function transformDonationBreakdown(breakdown: any[]) {
    console.log('Breakdown:', breakdown);
    return breakdown.map(item => ({
      uuid: item.uuid, // Include UUID for row click navigation
      employee_name: item.user?.full_name || 'N/A',
      employer_name: item.employer?.name || 'N/A',
      hours: item.leave_hours || 0,
      value: item.leave_value || 0,
      employer_top_up: item.employer_top_up || 0,
      date_donated: item.updated_at,
      status: item.donation_request_status?.name || 'Pending'
    }));
  }

  function handleRowClick(row: any) {
    // Navigate to donation request detail view
    if (row && row.uuid) {
      push(`/donation-request/${row.uuid}`);
    }
  }

  function openMarkReceivedConfirm() {
    markReceivedError = null;
    showMarkReceivedConfirm = true;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMarkReceivedConfirm();
    };
    markReceivedConfirmEscapeHandler = handler;
    window.addEventListener('keydown', handler);
  }

  function closeMarkReceivedConfirm() {
    if (markReceivedConfirmEscapeHandler) {
      window.removeEventListener('keydown', markReceivedConfirmEscapeHandler);
      markReceivedConfirmEscapeHandler = null;
    }
    showMarkReceivedConfirm = false;
  }

  async function handleMarkAsReceived() {
    if (!payoutDetail?.uuid || markingReceived) return;
    closeMarkReceivedConfirm();
    markReceivedError = null;
    markingReceived = true;
    try {
      const result = await PayoutService.markAsReceived(payoutDetail.uuid);
      if (result.success) {
        await loadPayoutDetails();
        await refreshCharityPendingConfirmation();
      } else {
        markReceivedError = result.error || 'Failed to mark payout as received.';
      }
    } catch (err) {
      console.error('Mark as received failed:', err);
      markReceivedError = 'Failed to mark payout as received.';
    } finally {
      markingReceived = false;
    }
  }

  async function handleDownloadReceipt() {
    if (!payoutDetail?.uuid) return;
    
    try {
      // Generate receipt PDF via API
      const response = await fetch(`/api/v1/charity/payout/${payoutDetail.uuid}/receipt`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Accept': 'application/pdf'
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `payout-receipt-${payoutDetail.payout_id || payoutDetail.uuid}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        // Fallback: open in new window if PDF endpoint doesn't exist yet
        window.open(`/api/v1/charity/payout/${payoutDetail.uuid}/receipt`, '_blank');
      }
    } catch (error) {
      console.error('Error downloading receipt:', error);
      // Fallback: try opening receipt page
      window.open(`/api/v1/charity/payout/${payoutDetail.uuid}/receipt`, '_blank');
    }
  }

  // Initialize component
  onMount(() => {
    loadPayoutDetails();
  });

</script>

<div class="payout-detail-container">
  <BackButton text="Back" onClick={() => window.history.back()} />
  <!-- Loading state -->
  {#if loading}
    <div class="loading-container">
      <div class="loading-spinner"></div>
      <p>Loading payout details...</p>
    </div>
  <!-- Error state -->
  {:else if error}
    <div class="error-container">
      <p class="error-message">{error}</p>
      <Button variant="primary" on:click={loadPayoutDetails}>Try Again</Button>
    </div>
  <!-- Content -->
  {:else if payoutDetail}
    {@const displayStatus = getDisplayStatus(payoutDetail)}
    <div class="detail-container">
      <!-- Payout Details Section -->
      <div class="info-section">
        <div class="section-header">
          <h2>Payout Details</h2>
          <div class="header-actions">
            <StageBadge {stage} />
            <div class="status-badge status-{getStatusClass(displayStatus)}">
              {@html getStatusIcon(displayStatus)}
              <span>{displayStatus}</span>
            </div>
          </div>
        </div>
        <div class="stepper-wrapper">
          <Stepper {stage} />
        </div>
        <div class="info-grid">
          <div class="info-item">
            <span class="info-label">Payout ID</span>
            <span>{payoutDetail.payout_id || `#P-${payoutDetail.uuid?.substring(0, 5) || ''}`}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Payout Date</span>
            <span>{formatDate(payoutDetail.payout_date)}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Total Donations</span>
            <span>{payoutDetail.total_donations || 0} Donations</span>
          </div>
          <div class="info-item">
            <span class="info-label">Company</span>
            {#if payoutDetail.employer?.uuid}
              <a href="#/charity/view-employer/{payoutDetail.employer.uuid}" class="company-link">{payoutDetail.company || payoutDetail.employer?.name || 'N/A'}</a>
            {:else}
              <span>{payoutDetail.company || 'N/A'}</span>
            {/if}
          </div>
          <div class="info-item">
            <span class="info-label">Payment Method</span>
            <span>{payoutDetail.payment_method || 'N/A'}</span>
          </div>
        </div>
      </div>

      <!-- Payment Summary Section -->
      {#if payoutDetail.payment_received || payoutDetail.l2g_fee_amount}
      <div class="info-section">
        <h2>Payment Summary</h2>
        <div class="info-grid">
          <div class="info-item">
            <span class="info-label">Employer payment</span>
            <span class="value-large">{formatCurrency(payoutDetail.payment_received || parseFloat(payoutDetail.total_amount || payoutDetail.amount || "0"))}</span>
          </div>
          {#if payoutDetail.l2g_fee_percentage}
          <div class="info-item">
            <span class="info-label">Transaction Fee</span>
            <span>{payoutDetail.l2g_fee_percentage}% ({formatCurrency(payoutDetail.l2g_fee_amount || 0)})</span>
          </div>
          {/if}
          {#if payoutDetail.net_payment}
          <div class="info-item">
            <span class="info-label">Net Payment to Charity</span>
            <span class="value-large net-payment">{formatCurrency(payoutDetail.net_payment)}</span>
          </div>
          {/if}
          {#if payoutDetail.payment_reference}
          <div class="info-item">
            <span class="info-label">L2G Payment Reference</span>
            <span class="reference-value">{payoutDetail.payment_reference}</span>
          </div>
          {/if}
          {#if payoutDetail.transaction_id}
          <div class="info-item">
            <span class="info-label">Transaction ID</span>
            <span>{payoutDetail.transaction_id}</span>
          </div>
          {/if}
          {#if payoutDetail.transaction_date}
          <div class="info-item">
            <span class="info-label">Transaction Date</span>
            <span>{formatDate(payoutDetail.transaction_date)}</span>
          </div>
          {/if}
        </div>
        {#if payoutDetail.bank_account && (payoutDetail.bank_account.bsb_no || payoutDetail.bank_account.acc_no || payoutDetail.bank_account.acc_name)}
        <div class="bank-details-section">
          <span class="section-label">Bank Account Details</span>
          <div class="bank-details">
            {#if payoutDetail.bank_account.bsb_no}
            <div class="bank-detail-item">
              <span class="bank-label">BSB:</span>
              <span class="bank-value">{payoutDetail.bank_account.bsb_no}</span>
            </div>
            {/if}
            {#if payoutDetail.bank_account.acc_no}
            <div class="bank-detail-item">
              <span class="bank-label">Account Number:</span>
              <span class="bank-value">{payoutDetail.bank_account.acc_no}</span>
            </div>
            {/if}
            {#if payoutDetail.bank_account.acc_name}
            <div class="bank-detail-item">
              <span class="bank-label">Account Name:</span>
              <span class="bank-value">{payoutDetail.bank_account.acc_name}</span>
            </div>
            {/if}
          </div>
        </div>
        {/if}
      </div>
      {/if}

      <!-- Donation Breakdown Section -->
      <div class="info-section">
        <h2>Donation Breakdown</h2>

        {#if loadingDonations}
          <div class="loading-donations">
            <div class="loading-spinner"></div>
            <p>Loading donations...</p>
          </div>
        {:else if donationBreakdown.length > 0}
        <Table
          columns={columns}
          data={transformDonationBreakdown(donationBreakdown)}
          onRowClick={handleRowClick}
        />

          <!-- Pagination -->
          {#if totalPages > 1}
            <div class="pagination-container">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                on:pageChange={handlePageChange}
              />
            </div>
          {/if}
        {:else}
          <div class="empty-donations">
            <p>No donation details available</p>
          </div>
        {/if}
      </div>

      <!-- Action Buttons -->
      <div class="action-buttons">
        {#if markReceivedError}
          <p class="mark-received-error" role="alert">{markReceivedError}</p>
        {/if}
        {#if (payoutDetail.donation_payout_status?.name?.toLowerCase() === 'completed' || payoutDetail.status?.name?.toLowerCase() === 'completed') && !payoutDetail.received && !payoutDetail.charity_marked_received_at}
          <Button class="btn-mark-received" variant="secondary" on:click={openMarkReceivedConfirm} disabled={markingReceived}>
            {markingReceived ? 'Saving...' : 'Mark as Received'}
          </Button>
        {/if}
        {#if payoutDetail.donation_payout_status?.name?.toLowerCase() === 'completed' || payoutDetail.status?.name?.toLowerCase() === 'completed'}
          <Button variant="primary" on:click={handleDownloadReceipt} disabled={loading}>
            {loading ? 'Downloading...' : 'Download Receipt (PDF)'}
          </Button>
        {/if}
      </div>
    </div>
  {/if}
</div>

<!-- Mark as Received confirmation modal -->
{#if showMarkReceivedConfirm}
  <div
    class="confirm-modal-backdrop"
    role="presentation"
    on:click|self={closeMarkReceivedConfirm}
    on:keydown={(e) => e.key === 'Escape' && closeMarkReceivedConfirm()}
  >
    <div class="confirm-modal-card" role="dialog" aria-modal="true" aria-labelledby="mark-received-confirm-title">
      <h2 id="mark-received-confirm-title" class="confirm-modal-title">Confirm mark as received</h2>
      <p class="confirm-modal-desc">
        Have you received this payout in your bank account? Marking it as received will update the payout status.
      </p>
      <div class="confirm-modal-actions">
        <button type="button" class="confirm-modal-cancel" on:click={closeMarkReceivedConfirm}>
          Cancel
        </button>
        <button type="button" class="confirm-modal-confirm" on:click={handleMarkAsReceived}>
          Yes, mark as received
        </button>
      </div>
    </div>
  </div>
{/if}

<style>
  .payout-detail-container {
    padding: 2rem;
    max-width: 1000px;
    margin: 0 auto;
    background: #f7fafc;
    min-height: 100vh;
  }

  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .loading-spinner {
    width: 2rem;
    height: 2rem;
    border: 3px solid #f3f4f6;
    border-top: 3px solid var(--color-primary, #f0b429);
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 1rem;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  .error-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    text-align: center;
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .error-message {
    color: #dc2626;
    font-size: 1.1rem;
    margin-bottom: 1rem;
  }

  .detail-container {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  .info-section {
    background: white;
    border-radius: 12px;
    padding: 1.5rem;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  }

  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
  }

  .section-header h2 {
    margin: 0;
  }

  .stepper-wrapper {
    margin-bottom: 1.5rem;
  }

  .header-actions {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .info-section h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #111827;
    margin: 0 0 1.5rem 0;
  }

  .info-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1rem;
  }

  .info-item {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .info-item .info-label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #6b7280;
  }

  .info-item span {
    font-size: 1rem;
    color: #111827;
    font-weight: 400;
  }

  .value-large {
    font-size: 1.25rem !important;
    font-weight: 600 !important;
    color: var(--color-positive, #059669) !important;
  }

  .value-large.net-payment {
    color: #059669 !important;
  }

  .reference-value {
    font-family: monospace !important;
    color: #6366f1 !important;
  }

  .bank-details-section {
    margin-top: 1.5rem;
    padding-top: 1.5rem;
    border-top: 1px solid #e5e7eb;
  }

  .bank-details-section > .section-label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #6b7280;
    display: block;
    margin-bottom: 0.75rem;
  }

  .bank-details {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .bank-detail-item {
    display: flex;
    gap: 0.5rem;
    align-items: baseline;
  }

  .bank-label {
    font-size: 0.875rem;
    color: #6b7280;
    font-weight: 500;
    min-width: 120px;
  }

  .bank-value {
    font-size: 0.875rem;
    color: #111827;
    font-weight: 600;
    font-family: monospace;
  }

  .pagination-container {
    margin-top: 2rem;
  }

  .loading-donations {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2rem;
    text-align: center;
    background: #f9fafb;
    border-radius: 8px;
  }

  .empty-donations {
    padding: 2rem;
    text-align: center;
    color: #6b7280;
    background-color: #f9fafb;
    border-radius: 8px;
  }

  .action-buttons {
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
    padding-top: 1rem;
    flex-wrap: wrap;
  }

  .action-buttons :global(button) {
    min-width: 200px;
  }

  /* Mark as Received: teal/secondary accent so it stands out from primary yellow */
  .mark-received-error {
    color: var(--color-negative, #FC6C6B);
    font-size: 0.875rem;
    margin: 0 0 0.75rem 0;
    width: 100%;
  }

  .action-buttons :global(.btn-mark-received) {
    background-color: var(--color-secondary, #2A9D8F) !important;
    color: var(--color-text-light, white) !important;
    border: none !important;
  }

  .action-buttons :global(.btn-mark-received:hover:not(:disabled)) {
    background-color: #248277 !important;
    color: var(--color-text-light, white) !important;
  }

  /* Mark as Received confirmation modal */
  .confirm-modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: var(--color-shade, rgba(0, 0, 0, 0.5));
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2000;
  }

  .confirm-modal-card {
    background: white;
    border-radius: 10px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
    padding: 1.5rem 2rem;
    max-width: 450px;
    width: 90%;
  }

  .confirm-modal-title {
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--color-text-dark, #374151);
    margin: 0 0 0.75rem 0;
  }

  .confirm-modal-desc {
    font-size: 0.875rem;
    color: #6b7280;
    margin: 0 0 1.5rem 0;
    line-height: 1.5;
  }

  .confirm-modal-actions {
    display: flex;
    gap: 0.75rem;
    justify-content: flex-end;
  }

  .confirm-modal-cancel {
    padding: 0.5rem 1rem;
    border-radius: var(--border-radius-button, 10px);
    font-size: 0.875rem;
    font-weight: 500;
    background: #f3f4f6;
    color: #374151;
    border: 1px solid #e5e7eb;
    cursor: pointer;
    transition: background-color 0.2s;
  }

  .confirm-modal-cancel:hover {
    background: #e5e7eb;
  }

  .confirm-modal-confirm {
    padding: 0.5rem 1rem;
    border-radius: var(--border-radius-button, 10px);
    font-size: 0.875rem;
    font-weight: 500;
    background: var(--color-secondary, #2A9D8F);
    color: var(--color-text-light, white);
    border: none;
    cursor: pointer;
    transition: background-color 0.2s;
  }

  .confirm-modal-confirm:hover {
    background: #248277;
  }

  :global(.donation-amount) {
    font-weight: 600;
    color: #111827;
  }

  .company-link {
    color: var(--color-link, #3b82f6);
    text-decoration: none;
    font-weight: 500;
  }

  .company-link:hover {
    text-decoration: underline;
    color: #2563eb;
  }

  .company-link:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.3);
    border-radius: 2px;
  }

  /* Status badges */
  :global(.status-badge) {
    display: inline-flex;
    align-items: center;
    padding: 0.25rem 0.75rem;
    border-radius: 9999px;
    font-size: 0.875rem;
    font-weight: 500;
    line-height: 1.5;
    width: fit-content;
    white-space: nowrap;
  }

  :global(.status-badge svg) {
    width: 16px;
    height: 16px;
    margin-right: 0.25rem;
    flex-shrink: 0;
    display: block;
  }

  :global(.status-badge span) {
    display: block;
    margin: 0;
    padding: 0;
  }

  :global(.status-approved) {
    background-color: #e6f4ea;
    color: #34a853;
  }

  :global(.status-pending) {
    background-color: #fff8e1;
    color: #f57c00;
  }

  :global(.status-pending-receival) {
    background-color: #dbeafe;
    color: #1e40af;
  }

  :global(.status-received) {
    background-color: #d1fae5;
    color: #065f46;
  }

  :global(.status-rejected) {
    background-color: #fee2e2;
    color: #991b1b;
  }

  @media (max-width: 768px) {
    .payout-detail-container {
      padding: 1rem;
    }

    .info-grid {
      grid-template-columns: 1fr;
    }

    .section-header {
      flex-direction: column;
      align-items: flex-start;
      gap: 0.75rem;
    }

    .action-buttons {
      flex-direction: column;
    }

    .action-buttons :global(button) {
      width: 100%;
    }
  }
</style> 